import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

const FOCUSABLE = 'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'

/**
 * Overlay drawer with a backdrop, Escape-to-close, focus trap, body-scroll
 * lock, and focus restoration. Slides in from the left or right. Rendered only
 * while `open`; the FD tokens apply because it mounts inside the `.fd-v2` tree.
 */
export default function Drawer({ open, onClose, side, title, children }) {
  const { t } = useTranslation()
  const panelRef = useRef(null)
  const restoreRef = useRef(null)

  useEffect(() => {
    if (!open) return undefined

    restoreRef.current = document.activeElement
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const panel = panelRef.current
    const focusables = () => Array.from(panel ? panel.querySelectorAll(FOCUSABLE) : [])
    // Focus the first focusable element (or the panel itself). preventScroll
    // stops the browser scrolling to the panel while it's still sliding in from
    // off-screen, which otherwise shifts the background sideways and back.
    const first = focusables()[0]
    if (first) first.focus({ preventScroll: true })
    else if (panel) panel.focus({ preventScroll: true })

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
        return
      }
      if (e.key === 'Tab') {
        const items = focusables()
        if (items.length === 0) {
          e.preventDefault()
          return
        }
        const firstEl = items[0]
        const lastEl = items[items.length - 1]
        if (e.shiftKey && document.activeElement === firstEl) {
          e.preventDefault()
          lastEl.focus({ preventScroll: true })
        } else if (!e.shiftKey && document.activeElement === lastEl) {
          e.preventDefault()
          firstEl.focus({ preventScroll: true })
        }
      }
    }

    document.addEventListener('keydown', onKeyDown, true)
    return () => {
      document.removeEventListener('keydown', onKeyDown, true)
      document.body.style.overflow = prevOverflow
      const restore = restoreRef.current
      if (restore && typeof restore.focus === 'function') restore.focus({ preventScroll: true })
    }
  }, [open, onClose])

  if (!open) return null

  return (
    <>
      <div className="fd-drawer-backdrop" onClick={onClose} />
      <div
        ref={panelRef}
        className={`fd-drawer fd-drawer--${side}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
      >
        <div className="fd-drawer__header">
          <span className="fd-eyebrow">{title}</span>
          <button className="fd-btn fd-btn--ghost fd-btn--icon fd-btn--sm" aria-label={t('Sentinel.V2.close')} onClick={onClose}>
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>
        </div>
        <div className="fd-drawer__body">{children}</div>
      </div>
    </>
  )
}

Drawer.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  side: PropTypes.oneOf(['left', 'right']).isRequired,
  title: PropTypes.string.isRequired,
  children: PropTypes.node,
}
